import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function formatNumber(value) {
  return numberFormatter.format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString();
}

function getAmount(record) {
  return Number(record.total_amount || record.amount || 0);
}

function approvalState(record) {
  const value = Number(record.approval);
  if (value === 1) return 'approved';
  if (value === 3) return 'needs_edit';
  return 'review';
}

function approvalLabel(t, record) {
  const state = approvalState(record);
  if (state === 'approved') return t('approved') || 'Approved';
  if (state === 'needs_edit') return t('needs_edit') || 'Needs edit';
  return t('under_review') || 'Under review';
}

function approvalColor(record) {
  const state = approvalState(record);
  if (state === 'approved') return { color: COLORS.green, bg: COLORS.greenSoft };
  if (state === 'needs_edit') return { color: COLORS.rose, bg: COLORS.roseSoft };
  return { color: COLORS.gold, bg: COLORS.goldSoft };
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: soft,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function RecordList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [workPlaceFilter, setWorkPlaceFilter] = useState('all');
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/vetclinic-records`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setRecords(rows.sort((a, b) => new Date(b.record_date || 0) - new Date(a.record_date || 0)));
    } catch (err) {
      setError(t('error_fetching_records') || 'Failed to fetch clinic records');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const summary = useMemo(() => {
    const approved = records.filter((record) => approvalState(record) === 'approved').length;
    const needsEdit = records.filter((record) => approvalState(record) === 'needs_edit').length;
    const inside = records.filter((record) => String(record.work_place || '').toLowerCase() === 'inside').length;
    return {
      total: records.length,
      approved,
      pending: Math.max(records.length - approved - needsEdit, 0),
      needsEdit,
      inside,
      amount: records.reduce((sum, record) => sum + getAmount(record), 0),
    };
  }, [records]);

  const filteredRecords = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return records.filter((record) => {
      const stateOk = statusFilter === 'all' || approvalState(record) === statusFilter;
      const workPlace = String(record.work_place || '').toLowerCase();
      const workOk = workPlaceFilter === 'all' || workPlace === workPlaceFilter;
      const searchOk =
        !needle ||
        [
          record.record_id,
          record.horse_name,
          record.client_name,
          record.work_place,
          record.notes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      return stateOk && workOk && searchOk;
    });
  }, [records, search, statusFilter, workPlaceFilter]);

  const pagedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDeleteClick = (recordId) => {
    setDeleteRecordId(recordId);
    setDeleteConfirmationOpen(true);
    setConfirmationText('');
  };

  const confirmDelete = async () => {
    if (confirmationText.toLowerCase() !== 'delete') {
      setError(t('please_type_delete_to_confirm') || 'Please type delete to confirm');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/vetclinic-records/${deleteRecordId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setRecords((prev) => prev.filter((record) => record.record_id !== deleteRecordId));
      setDeleteConfirmationOpen(false);
      setConfirmationText('');
    } catch (err) {
      setError(t('error_deleting_record') || 'Failed to delete record');
    }
  };

  return (
    <Box sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1540, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('vetclinic_records') || 'Clinic Records'}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {t('clinic_records_hint') ||
                  'Centralized health records with quick review, approval status, and accounting-ready totals.'}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchRecords}
                sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 800 }}
              >
                {t('refresh') || 'Refresh'}
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeOutlinedIcon />}
                onClick={() => navigate('/VetClinicRecordInside')}
                sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
              >
                {t('Add_Inside_Record') || 'Inside record'}
              </Button>
              <Button
                variant="contained"
                startIcon={<ExitToAppOutlinedIcon />}
                onClick={() => navigate('/VetClinicRecordOutside')}
                sx={{ bgcolor: COLORS.blue, fontWeight: 900, '&:hover': { bgcolor: '#5f7f95' } }}
              >
                {t('Add_Outside_Record') || 'Outside record'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <MetricCard icon={<HealthAndSafetyIcon />} title={t('all_records') || 'All records'} value={formatNumber(summary.total)} helper={`${formatNumber(summary.amount)} SAR`} />
          <MetricCard icon={<HomeOutlinedIcon />} title={t('inside_records') || 'Inside records'} value={formatNumber(summary.inside)} helper={t('stable_cases') || 'Stable cases'} color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<HealthAndSafetyIcon />} title={t('approved') || 'Approved'} value={formatNumber(summary.approved)} helper={`${summary.total ? Math.round((summary.approved / summary.total) * 100) : 0}%`} color={COLORS.green} soft={COLORS.greenSoft} />
          <MetricCard icon={<EditIcon />} title={t('needs_edit') || 'Needs edit'} value={formatNumber(summary.needsEdit)} helper={`${formatNumber(summary.pending)} ${t('under_review') || 'under review'}`} color={COLORS.rose} soft={COLORS.roseSoft} />
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1.25}
            sx={{ p: 2 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder={t('search') || 'Search'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 300 } }}
              />
              <TextField
                select
                size="small"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(0);
                }}
                label={t('approval_status') || 'Approval'}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="all">{t('show_all') || 'All'}</MenuItem>
                <MenuItem value="review">{t('under_review') || 'Under review'}</MenuItem>
                <MenuItem value="approved">{t('approved') || 'Approved'}</MenuItem>
                <MenuItem value="needs_edit">{t('needs_edit') || 'Needs edit'}</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                value={workPlaceFilter}
                onChange={(event) => {
                  setWorkPlaceFilter(event.target.value);
                  setPage(0);
                }}
                label={t('work_place') || 'Work place'}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">{t('show_all') || 'All'}</MenuItem>
                <MenuItem value="inside">{t('inside') || 'Inside'}</MenuItem>
                <MenuItem value="outside">{t('outside') || 'Outside'}</MenuItem>
              </TextField>
            </Stack>
            <Chip
              label={`${formatNumber(filteredRecords.length)} ${t('records') || 'records'}`}
              sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900, alignSelf: { xs: 'flex-start', md: 'center' } }}
            />
          </Stack>

          <TableContainer sx={smartTableContainerSx}>
            <Table size="small" sx={smartTableSx}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell width={70}>#</TableCell>
                  <TableCell width={90}>ID</TableCell>
                  <TableCell width={130}>{t('record_date') || 'Date'}</TableCell>
                  <TableCell>{t('horse_name') || 'Horse'}</TableCell>
                  <TableCell align="right" width={120}>{t('total_amount') || 'Amount'}</TableCell>
                  <TableCell width={140}>{t('work_place') || 'Work place'}</TableCell>
                  <TableCell width={170}>{t('approval_status') || 'Approval'}</TableCell>
                  <TableCell align="center" width={145}>{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : pagedRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: COLORS.muted }}>
                      {t('no_data') || 'No data'}
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedRecords.map((record, index) => {
                    const status = approvalColor(record);
                    return (
                      <TableRow key={record.record_id} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{record.record_id}</TableCell>
                        <TableCell>{formatDate(record.record_date)}</TableCell>
                        <TableCell>
                          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                            {record.horse_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(getAmount(record))} SAR</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={record.work_place || '-'}
                            sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text, fontWeight: 800 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={approvalLabel(t, record)}
                            sx={{ bgcolor: status.bg, color: status.color, fontWeight: 900 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t('view') || 'View'}>
                            <IconButton onClick={() => navigate(`/record-details/${record.record_id}`)} sx={{ color: COLORS.blue }}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('edit') || 'Edit'}>
                            <IconButton onClick={() => navigate(`/VetClinicRecordEdit/${record.record_id}`)} sx={{ color: COLORS.gold }}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('delete') || 'Delete'}>
                            <IconButton onClick={() => handleDeleteClick(record.record_id)} sx={{ color: COLORS.rose }}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRecords.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 15, 25, 50]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
          />
        </Paper>
      </Box>

      <Dialog open={deleteConfirmationOpen} onClose={() => setDeleteConfirmationOpen(false)}>
        <DialogTitle>{t('confirm_deletion') || 'Confirm deletion'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.text, mb: 1 }}>
            {t('please_type_delete_to_confirm') || 'Please type delete to confirm.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={confirmationText}
            onChange={(event) => setConfirmationText(event.target.value)}
            label={t('type_delete') || 'Type delete'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmationOpen(false)}>{t('cancel') || 'Cancel'}</Button>
          <Button onClick={confirmDelete} sx={{ color: COLORS.rose, fontWeight: 900 }}>
            {t('delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
